import { useEffect, useMemo, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useInfiniteQuery, useQueryClient } from "@tanstack/react-query";
import {
  collection,
  getDocs,
  doc,
  getDoc,
  updateDoc,
  orderBy,
  query,
  serverTimestamp,
  limit,
  startAfter,
} from "firebase/firestore";
import {
  Flag,
  Search,
  X,
  CheckCircle2,
  Clock,
  Eye,
  Loader2,
  Ban,
  User,
} from "lucide-react";
import { db } from "../../firebase";
import AdminLayout from "../layout/AdminLayout";
import toast from "react-hot-toast";

const statusTabs = [
  { key: "all", label: "All" },
  { key: "pending", label: "Pending" },
  { key: "reviewed", label: "Reviewed" },
  { key: "dismissed", label: "Dismissed" },
];

export default function Reports() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("pending");
  const sentinelRef = useRef(null);
  const PAGE_SIZE = 200;

  const {
    data: reportsData,
    isLoading: loading,
    isFetchingNextPage: loadingMore,
    fetchNextPage,
    hasNextPage,
  } = useInfiniteQuery({
    queryKey: ["adminReportsPageData"],
    queryFn: async ({ pageParam = null }) => {
      const q = pageParam
        ? query(collection(db, "reports"), orderBy("createdAt", "desc"), startAfter(pageParam), limit(PAGE_SIZE))
        : query(collection(db, "reports"), orderBy("createdAt", "desc"), limit(PAGE_SIZE));
      const snap = await getDocs(q);
      const pageReports = snap.docs.map((d) => ({ id: d.id, ...d.data() }));

      const uids = new Set();
      pageReports.forEach((r) => {
        if (r.reportedUserId) uids.add(r.reportedUserId);
        if (r.reportedByUserId) uids.add(r.reportedByUserId);
      });
      const profileMap = {};
      await Promise.all(
        [...uids].map(async (uid) => {
          try {
            const pSnap = await getDoc(doc(db, "profiles", uid));
            if (pSnap.exists()) profileMap[uid] = { id: pSnap.id, ...pSnap.data() };
          } catch {
            // ignore
          }
        })
      );

      return { reports: pageReports, profiles: profileMap, nextCursor: snap.docs.length === PAGE_SIZE ? snap.docs[snap.docs.length - 1] : null };
    },
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    staleTime: 300000,
    cacheTime: 1800000,
    retry: 1,
  });

  const reports = reportsData?.pages.flatMap((p) => p.reports) || [];
  const profiles = Object.assign({}, ...(reportsData?.pages.map((p) => p.profiles) || []));
  const hasMore = !!hasNextPage;

  useEffect(() => {
    if (!sentinelRef.current || !hasMore || loading || loadingMore) return undefined;
    const observer = new IntersectionObserver((entries) => {
      if (!entries[0]?.isIntersecting) return;
      fetchNextPage().catch(() => {});
    }, { rootMargin: "300px 0px" });
    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [hasMore, loading, loadingMore, fetchNextPage]);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return reports.filter((r) => {
      if (filter !== "all" && r.status !== filter) return false;
      if (term) {
        const searchable = [r.reportedUserName, r.reason, r.reportedUserId, r.reportedByUserId]
          .filter(Boolean).join(" ").toLowerCase();
        if (!searchable.includes(term)) return false;
      }
      return true;
    });
  }, [reports, search, filter]);

  const updateStatus = async (report, status) => {
    try {
      await updateDoc(doc(db, "reports", report.id), {
        status,
        reviewedAt: serverTimestamp(),
      });
      queryClient.setQueryData(["adminReportsPageData"], (oldData) => {
        if (!oldData) return oldData;
        return {
          ...oldData,
          pages: oldData.pages.map((page) => ({
            ...page,
            reports: page.reports.map((r) => (r.id === report.id ? { ...r, status } : r)),
          })),
        };
      });
      toast.success(`Report marked as ${status}`);
    } catch {
      toast.error("Could not update report.");
    }
  };

  const counts = useMemo(() => ({
    all: reports.length,
    pending: reports.filter((r) => r.status === "pending").length,
    reviewed: reports.filter((r) => r.status === "reviewed").length,
    dismissed: reports.filter((r) => r.status === "dismissed").length,
  }), [reports]);

  return (
    <AdminLayout>
      <div className="mb-5">
        <p className="section-subtle">Review</p>
        <h1 className="mt-1 font-display text-3xl font-bold text-ink-800">Reports</h1>
        <p className="mt-1 text-[13.5px] text-ink-400">
          User-submitted profile reports. Review and take action.
        </p>
      </div>

      <div className="card p-4 sm:p-5">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <div className="input-group flex-1">
            <Search size={16} className="text-ink-400" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name, reason, user ID..."
            />
            {search && (
              <button onClick={() => setSearch("")} className="text-ink-400 hover:text-brand-600">
                <X size={14} />
              </button>
            )}
          </div>
        </div>

        <div className="-mx-1 mt-4 flex flex-wrap gap-2">
          {statusTabs.map((t) => {
            const active = filter === t.key;
            return (
              <button
                key={t.key}
                type="button"
                onClick={() => setFilter(t.key)}
                className={`group inline-flex items-center gap-2 rounded-2xl px-3.5 py-1.5 text-[12.5px] font-semibold transition ${
                  active
                    ? "bg-brand-gradient text-white shadow-soft"
                    : "border border-sand-200 bg-white text-ink-700 hover:border-brand-600 hover:text-brand-600"
                }`}
              >
                {t.label}
                <span className={`inline-flex min-w-5 items-center justify-center rounded-full px-1.5 text-[10.5px] ${
                  active ? "bg-white/25 text-white" : "bg-sand-100 text-ink-500"
                }`}>
                  {counts[t.key]}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      <div className="mt-5 space-y-3">
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="animate-spin text-brand-600" size={28} />
          </div>
        ) : filtered.length === 0 ? (
          <div className="card flex flex-col items-center px-6 py-12 text-center">
            <Flag className="text-ink-300" size={32} />
            <p className="mt-2 text-[13px] font-semibold text-ink-600">No reports found</p>
            <p className="mt-1 text-[12px] text-ink-400">Reports submitted by users will appear here.</p>
          </div>
        ) : (
          filtered.map((report) => (
            <motion.article
              key={report.id}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              className="card overflow-hidden"
            >
              <div className="p-4">
                {/* Reason badge */}
                <div className="mb-3 flex items-center justify-between">
                  <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2.5 py-1 text-[11.5px] font-semibold text-amber-700">
                    <Flag size={11} /> {report.reason}
                  </span>
                  <span className={`shrink-0 rounded-full px-2.5 py-0.5 text-[10.5px] font-bold uppercase ${
                    report.status === "pending" ? "bg-amber-50 text-amber-700" :
                    report.status === "reviewed" ? "bg-emerald-50 text-emerald-700" :
                    "bg-sand-100 text-ink-500"
                  }`}>
                    {report.status === "pending" && <Clock size={10} className="mr-0.5 inline" />}
                    {report.status === "reviewed" && <CheckCircle2 size={10} className="mr-0.5 inline" />}
                    {report.status}
                  </span>
                </div>

                {/* Reported user */}
                <div className="rounded-2xl border border-rose-100 bg-rose-50/30 p-3">
                  <p className="mb-2 text-[10px] font-bold uppercase tracking-wider text-rose-500">Reported profile</p>
                  <ProfileRow profile={profiles[report.reportedUserId]} uid={report.reportedUserId} navigate={navigate} />
                </div>

                {/* Reporter */}
                <div className="mt-2 rounded-2xl border border-sand-200 bg-sand-50/30 p-3">
                  <p className="mb-2 text-[10px] font-bold uppercase tracking-wider text-ink-400">Reported by</p>
                  <ProfileRow profile={profiles[report.reportedByUserId]} uid={report.reportedByUserId} navigate={navigate} />
                </div>
              </div>

              <div className="flex border-t border-sand-100">
                <button
                  type="button"
                  onClick={() => navigate(`/admin/users/${report.reportedUserId}`)}
                  className="flex flex-1 items-center justify-center gap-1.5 border-r border-sand-100 py-2.5 text-[12.5px] font-semibold text-ink-600 transition hover:bg-sand-50 hover:text-brand-600"
                >
                  <Eye size={13} /> View reported
                </button>
                {report.status === "pending" && (
                  <>
                    <button
                      type="button"
                      onClick={() => updateStatus(report, "reviewed")}
                      className="flex flex-1 items-center justify-center gap-1.5 border-r border-sand-100 py-2.5 text-[12.5px] font-semibold text-emerald-600 transition hover:bg-emerald-50"
                    >
                      <CheckCircle2 size={13} /> Reviewed
                    </button>
                    <button
                      type="button"
                      onClick={() => updateStatus(report, "dismissed")}
                      className="flex flex-1 items-center justify-center gap-1.5 py-2.5 text-[12.5px] font-semibold text-ink-400 transition hover:bg-sand-50"
                    >
                      <Ban size={13} /> Dismiss
                    </button>
                  </>
                )}
              </div>
            </motion.article>
          ))
        )}
      </div>
    </AdminLayout>
  );
}

function ProfileRow({ profile, uid, navigate }) {
  const photo = profile?.imageUrls?.[0];
  return (
    <button
      type="button"
      onClick={() => profile && navigate(`/admin/users/${uid}`)}
      className="flex w-full items-center gap-3 text-left"
    >
      {photo && !profile?.hidePhotos ? (
        <img src={photo} alt={profile?.fullName} className="h-11 w-11 shrink-0 rounded-xl object-cover object-center ring-1 ring-sand-200" />
      ) : (
        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-sand-100 text-ink-400">
          <User size={18} />
        </div>
      )}
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5">
          <p className="truncate text-[13.5px] font-bold text-ink-800">{profile?.fullName || "Unknown"}</p>
          {profile?.kmProfileId && (
            <span className="shrink-0 rounded-full bg-brand-50 px-1.5 py-0.5 text-[10px] font-bold text-brand-600">{profile.kmProfileId}</span>
          )}
        </div>
        <p className="truncate text-[11.5px] text-ink-400">
          {[profile?.profession, profile?.presentResidence || profile?.state].filter(Boolean).join(" • ") || uid?.slice(0, 16) + "..."}
        </p>
      </div>
    </button>
  );
}
